import type { SearchResponse } from '@algolia/client-search';
import invariant from 'invariant';
import { useCallback, useMemo } from 'react';
import {
  type ContainerProps,
  type SingleValue,
  components,
} from 'react-select';
import AsyncSelect, { type AsyncProps } from 'react-select/async';

import type { OptionType } from '#/components/Select/Select.types';
import { useIsUsingAlgoliaMock } from '#/config/AppConfigContext';
import { ClientOnly } from '#/containers/ClientOnly';
import {
  type AlgoliaSearchArgs,
  algoliaSearch,
} from '#/utils/algolia/algoliaSearch';

type PassableReactSelectProps<T> = Pick<
  AsyncProps<OptionType<T>, false, never>,
  | 'filterOption'
  | 'formatOptionLabel'
  | 'getOptionLabel'
  | 'getOptionValue'
  | 'noOptionsMessage'
  | 'onBlur'
  | 'defaultValue'
  | 'placeholder'
  | 'autoFocus'
  | 'className'
>;

export type AlgoliaSearchSelectProps<T> = {
  qaId?: string;
  disabled?: boolean;
  onChange?: (newValue: SingleValue<OptionType<T>>) => void;
  searchArgs?: Partial<AlgoliaSearchArgs>;
  responseToOptions?: (hits: SearchResponse<T>['hits']) => OptionType<T>[];
} & PassableReactSelectProps<T>;

function defaultComponents<T>(args: { qaId?: string }) {
  return {
    SelectContainer: ({
      children,
      ...rest
    }: ContainerProps<OptionType<T>, false, never>) => (
      <components.SelectContainer
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...rest}
        innerProps={
          (args.qaId
            ? { 'data-qa-id': args.qaId }
            : {}) as React.HTMLAttributes<HTMLDivElement>
        }
      >
        {children}
      </components.SelectContainer>
    ),
  };
}

export function AlgoliaSearchSelect<T>(props: AlgoliaSearchSelectProps<T>) {
  const { isUsingAlgoliaMock } = useIsUsingAlgoliaMock();

  const {
    qaId,
    className,
    placeholder,
    disabled,
    searchArgs,
    defaultValue,
    filterOption,
    formatOptionLabel,
    getOptionLabel,
    getOptionValue,
    onChange,
    onBlur,
    noOptionsMessage,
    responseToOptions = (hits) =>
      hits.map((hit) => ({ label: hit.objectID, value: hit })),
  } = props;

  const classNames = useMemo(() => {
    return [className, qaId].filter(Boolean).join(' ');
  }, [className, qaId]);

  const loadOptions = useCallback(
    async (inputValue: string) => {
      const query = searchArgs?.query ?? inputValue;
      invariant(searchArgs?.config, 'Algolia config must be provided');
      invariant(query, 'Algolia query must be provided');
      invariant(searchArgs?.indexName, 'Algolia indexName must be provided');
      invariant(searchArgs?.options, 'Algolia options must be provided');

      const { hits } = await algoliaSearch<T>({
        config: searchArgs.config,
        indexName: searchArgs.indexName,
        query,
        options: searchArgs.options,
        analytics: searchArgs?.analytics,
        onApiKeyTimeout: searchArgs?.onApiKeyTimeout,
        isUsingAlgoliaMock,
      });

      return responseToOptions(hits);
    },
    [searchArgs, responseToOptions, isUsingAlgoliaMock],
  );

  return (
    <ClientOnly>
      <AsyncSelect
        defaultValue={defaultValue}
        onChange={onChange}
        onBlur={onBlur}
        noOptionsMessage={noOptionsMessage}
        filterOption={filterOption}
        formatOptionLabel={formatOptionLabel}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        loadOptions={loadOptions}
        className={classNames}
        placeholder={placeholder}
        isDisabled={disabled}
        components={defaultComponents<T>({ qaId })}
      />
    </ClientOnly>
  );
}
